import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore, MoreVert, Edit, Delete } from '@mui/icons-material';
import { CommentBubble } from './CommentBubble';
import { CommentCurvedConnector } from './CommentLineConnector';
import { CommentInput } from '../Comments/CommentsInput';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { getJWtDetails } from '../../utils/AppUtils';
import { IFeedComment } from '../../redux/activity/types';
import { FONT_ENUM } from '../../types/global/fonts.types';

export interface CommentThreadProps {
    comment: IFeedComment;
    onLike: (comment: IFeedComment) => void;
    onReply: (comment: IFeedComment, replyText: string) => void;
    onEdit: (comment: IFeedComment, newText: string) => void;
    onDelete: (comment: IFeedComment) => void;
    onShowRepliesMobile?: (comment: IFeedComment) => void;
    isMobile?: boolean;
    depth?: number;
    activeReplyId?: string | null;
    onSetActiveReply?: (commentId: string | null) => void;
    loadMoreReplies?: (comment: IFeedComment) => void;
    fetchingMoreReplies?: boolean;
}

const SingleComment: React.FC<{
    comment: IFeedComment;
    onLike: (comment: IFeedComment) => void;
    onReply: (comment: IFeedComment, replyText: string) => void;
    onEdit: (comment: IFeedComment, newText: string) => void;
    onDelete: (comment: IFeedComment) => void;
    depth: number;
    isLast?: boolean;
    showConnector?: boolean;
    activeReplyId?: string | null;
    onSetActiveReply?: (commentId: string | null) => void;
    loadMoreReplies?: (parentComment: IFeedComment) => void;
    fetchingMoreReplies?: boolean;
}> = ({
    comment,
    onLike,
    onReply,
    onEdit,
    onDelete,
    depth,
    isLast = false,
    showConnector = false,
    activeReplyId,
    onSetActiveReply,
    loadMoreReplies,
    fetchingMoreReplies,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const editInputRef = React.useRef<any>(null);
    const hasReplies = comment.first_replies && comment.first_replies.length > 0;
    const { themeStyle } = useThemeContext();
    const { user_id } = getJWtDetails();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    // Maximum depth for nested replies (5 layers deep)
    const MAX_REPLY_DEPTH = 5;
    const canReply = depth < MAX_REPLY_DEPTH;

    // Check if current user owns this comment
    const isOwner = user_id === comment.created_by._id;

    // Check if this comment's reply input is active
    const isReplyActive = activeReplyId === comment._id;

    const handleReplyClick = () => {
        if (!canReply) return; // Don't allow replies at max depth
        if (onSetActiveReply) {
            // Toggle: if this reply is active, hide it; otherwise show it
            onSetActiveReply(isReplyActive ? null : comment._id);
        }
    };

    const handleReplySubmit = (replyText: string) => {
        onReply(comment, replyText);
        if (onSetActiveReply) {
            onSetActiveReply(null); // Hide reply input after sending
        }
    };

    const handleReplyCancel = () => {
        if (onSetActiveReply) {
            onSetActiveReply(null); // Hide reply input on cancel
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
        setMenuAnchor(null);
        // Focus the input after state update, especially important on mobile
        setTimeout(() => {
            editInputRef.current?.focus();
        }, 100);
    };

    const handleEditSubmit = (newText: string) => {
        onEdit(comment, newText);
        setIsEditing(false);
    };

    const handleEditCancel = () => {
        setIsEditing(false);
    };

    const handleDeleteClick = () => {
        onDelete(comment);
        setMenuAnchor(null);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleLoadMoreReplies = () => {
        loadMoreReplies && loadMoreReplies(comment);
    };

    return (
        <Box sx={{ position: 'relative', ml: depth > 0 ? 4 : 0, mb: 2 }}>
            {showConnector && <CommentCurvedConnector verticalOffset={18} horizontalOffset={35} animate={true} />}

            <Box sx={{ display: 'flex', alignItems: 'flex-start', position: 'relative' }}>
                <Box sx={{ flex: 1 }}>
                    {isEditing ? (
                        <CommentInput
                            ref={editInputRef}
                            onCommentSend={handleEditSubmit}
                            onCloseClick={handleEditCancel}
                            defaultValue={comment.comment}
                            showClear={true}
                            placeHolder="Edit your comment..."
                        />
                    ) : (
                        <CommentBubble
                            user={comment.created_by}
                            comment={comment.comment}
                            createdAt={comment.createdAt}
                            totalLikes={comment.total_likes || 0}
                            isUserLiked={comment.is_user_liked}
                            onLike={() => onLike(comment)}
                            onReply={handleReplyClick}
                            canReply={canReply}
                        />
                    )}
                </Box>

                {/* Menu for owner actions */}
                {isOwner && !isEditing && (
                    <Box>
                        {isMobile ? (
                            // Mobile: Show edit and delete icons directly
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <IconButton
                                    size="small"
                                    onClick={handleEditClick}
                                    sx={{
                                        minWidth: '32px',
                                        minHeight: '32px',
                                        padding: '4px',
                                        '&:hover': {
                                            backgroundColor: `${theme[themeStyle].tag_white}10`,
                                        },
                                        '&:active': {
                                            backgroundColor: `${theme[themeStyle].tag_white}20`,
                                            transform: 'scale(0.95)',
                                        },
                                        WebkitTapHighlightColor: 'transparent',
                                        transition: 'all 0.2s ease',
                                    }}>
                                    <Edit fontSize="small" sx={{ color: theme[themeStyle].tag_white }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={handleDeleteClick}
                                    sx={{
                                        minWidth: '32px',
                                        minHeight: '32px',
                                        padding: '4px',
                                        '&:hover': {
                                            backgroundColor: '#ff444410',
                                        },
                                        '&:active': {
                                            backgroundColor: '#ff444420',
                                            transform: 'scale(0.95)',
                                        },
                                        WebkitTapHighlightColor: 'transparent',
                                        transition: 'all 0.2s ease',
                                    }}>
                                    <Delete fontSize="small" sx={{ color: '#ff4444' }} />
                                </IconButton>
                            </Box>
                        ) : (
                            // Desktop: Show MoreVert menu
                            <>
                                <IconButton size="small" onClick={handleMenuOpen}>
                                    <MoreVert fontSize="small" sx={{ color: theme[themeStyle].tag_white }} />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={handleMenuClose}
                                    PaperProps={{
                                        sx: {
                                            bgcolor: theme[themeStyle].form_gray,
                                            border: `1px solid ${theme[themeStyle].tag_white}`,
                                        },
                                    }}>
                                    <MenuItem
                                        onClick={handleEditClick}
                                        sx={{ color: theme[themeStyle].tag_white, fontSize: 14 }}>
                                        Edit
                                    </MenuItem>
                                    <MenuItem onClick={handleDeleteClick} sx={{ color: '#ff4444', fontSize: 14 }}>
                                        Delete
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {/* Reply Input - only show if this comment is the active reply and replies are allowed */}
            {isReplyActive && canReply && (
                <Box sx={{ mt: 1, ml: 2 }}>
                    <CommentInput
                        onCommentSend={handleReplySubmit}
                        onCloseClick={handleReplyCancel}
                        showClear={true}
                        placeHolder="Write a reply..."
                    />
                </Box>
            )}

            {hasReplies && (
                <Box sx={{ mt: 1 }}>
                    <Box display="flex" alignItems="center" sx={{ ml: 1 }}>
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            {expanded ? (
                                <ExpandLess fontSize="small" sx={{ color: theme[themeStyle].tag_white }} />
                            ) : (
                                <ExpandMore fontSize="small" sx={{ color: theme[themeStyle].tag_white }} />
                            )}
                        </IconButton>
                        <Typography variant="caption" sx={{ color: theme[themeStyle].tag_white }}>
                            {comment.total_replies} {comment.total_replies === 1 ? 'reply' : 'replies'}
                        </Typography>
                    </Box>

                    <Collapse in={expanded}>
                        <Box sx={{ mt: 1, position: 'relative' }}>
                            {comment.first_replies.map((reply, index) => (
                                <Box key={reply._id} sx={{ position: 'relative', mb: 1 }}>
                                    <SingleComment
                                        comment={reply}
                                        onLike={onLike}
                                        onReply={onReply}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                        depth={depth + 1}
                                        isLast={index === comment.first_replies.length - 1}
                                        showConnector={true}
                                        activeReplyId={activeReplyId}
                                        onSetActiveReply={onSetActiveReply}
                                        loadMoreReplies={loadMoreReplies}
                                        fetchingMoreReplies={fetchingMoreReplies}
                                    />
                                </Box>
                            ))}
                        </Box>
                        {/* Load More Replies Buttons - only show if total_replies > first_replies.length */}
                        {comment.pagination?.hasNextPage && (
                            <Box
                                sx={{ width: '100%', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
                                <Typography
                                    onClick={() => handleLoadMoreReplies()}
                                    sx={{
                                        color: theme[themeStyle].primary,
                                        fontSize: '0.75rem',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        cursor: 'pointer',
                                    }}>
                                    {fetchingMoreReplies
                                        ? 'Loading replies...'
                                        : `Load more replies (${
                                              comment.total_replies - comment.first_replies.length
                                          } remaining)`}
                                </Typography>
                            </Box>
                        )}
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

export const CommentThread: React.FC<CommentThreadProps> = ({
    comment,
    onLike,
    onReply,
    onEdit,
    onDelete,
    onShowRepliesMobile,
    isMobile,
    depth = 0,
    activeReplyId,
    onSetActiveReply,
    loadMoreReplies,
    fetchingMoreReplies,
}) => {
    return (
        <SingleComment
            comment={comment}
            onLike={onLike}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            depth={depth}
            showConnector={false}
            activeReplyId={activeReplyId}
            onSetActiveReply={onSetActiveReply}
            loadMoreReplies={comment => {
                loadMoreReplies && loadMoreReplies(comment);
            }}
            fetchingMoreReplies={fetchingMoreReplies}
        />
    );
};
